import { useState, useEffect } from "react";
import { useAuth } from "../../../context/contextAuth.js";
import {
  getConfigAssets,
  getConfigScanProgress,
} from "../../../api/get/aws/get-config.js";
import { scanConfig } from "../../../api/post/post-aws.js";

const useAwsConfig = () => {
  const { user, userId } = useAuth();
  const [index, setIndex] = useState(0);
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");

  const [configLoading, setConfigLoading] = useState(false);
  const [configScanLoading, setConfigScanLoading] = useState(false);
  const [configScanProgress, setConfigScanProgress] = useState(null);
  const [configView, setConfigView] = useState(false);

  const [configAssets, setConfigAssets] = useState(null);
  const [cAwsConfigAll, setCAwsConfigAll] = useState(null);
  const [saveScore, setSaveScore] = useState(0);
  const [error, setError] = useState(null);

  const [dashboard, setDashboard] = useState(null);
  const [service, setService] = useState(null);
  const [region, setRegion] = useState(null);

  useEffect(() => {
    if (!user || !userId) return;
    (async () => {
      try {
        setConfigLoading(true);
        await fetchConfigAssets(userId);
      } catch (e) {
        console.error("Error in initData:", e);
      } finally {
        setConfigLoading(false);
      }
    })();
  }, [user, userId]);

  const initData = async () => {
    if (!user || !userId) return;
    return fetchConfigAssets(userId);
  };

  const fetchConfigAssets = async (userId) => {
    try {
      const assets = await getConfigAssets(userId);

      setConfigAssets(assets || []);
      setCAwsConfigAll(assets?.[0]?.misconfig || null);
      setSaveScore(assets?.[0]?.ss ?? 0);

      return assets;
    } catch (e) {
      console.error("Error loading config assets:", e);
      setConfigAssets([]);
      setCAwsConfigAll(null);
      setSaveScore(0);
      throw e;
    }
  };

  const refreshLatestAssessment = async (expectedScanId) => {
    let latestAssets = [];
    for (let attempt = 0; attempt < 5; attempt += 1) {
      latestAssets = await fetchConfigAssets(userId);
      if (!expectedScanId || latestAssets?.[0]?.aws_uuid === expectedScanId) {
        return latestAssets;
      }
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    throw new Error("The assessment completed, but the newest result is not available yet");
  };

  const handleConfigScan = async (e) => {
    e.preventDefault();
    setConfigScanLoading(true);
    setError(null);
    setConfigScanProgress({
      status: "running",
      stage: "starting",
      progress: 1,
      message: "Starting AWS assessment",
    });
    const progressTimer = window.setInterval(async () => {
      try {
        const progress = await getConfigScanProgress(userId);
        if (progress) setConfigScanProgress(progress);
      } catch (progressError) {
        console.warn("Unable to refresh AWS scan progress:", progressError);
      }
    }, 1000);

    try {
      const result = await scanConfig(userId, { accessKey, secretKey });
      const finalProgress = await getConfigScanProgress(userId);
      if (finalProgress) setConfigScanProgress(finalProgress);
      await refreshLatestAssessment(result?.data?.scanId);
      setConfigView(true);
    } catch (err) {
      setConfigView(false);
      setError({
        message: err.message,
        card: err.card || false
      });
      return;
    } finally {
      window.clearInterval(progressTimer);
      setConfigScanLoading(false);
      setAccessKey("");
      setSecretKey("");
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const resetState = () => {
    setIndex(0);
    setAccessKey("");
    setSecretKey("");
    setConfigLoading(false);
    setConfigScanLoading(false);
    setConfigScanProgress(null);
    setConfigView(false);
    setConfigAssets([]);
    setCAwsConfigAll(null);
    setSaveScore(0);

    setError(null);
    setDashboard(false);
    setService(false);
    setRegion(false);
  };

  return {
    index,
    setIndex,
    accessKey,
    setAccessKey,
    secretKey,
    setSecretKey,

    configLoading,
    setConfigLoading,
    configScanLoading,
    setConfigScanLoading,
    configScanProgress,
    setConfigScanProgress,
    configView,
    setConfigView,

    configAssets,
    setConfigAssets,
    cAwsConfigAll,
    setCAwsConfigAll,

    error,
    setError,
    handleConfigScan,
    fetchConfigAssets,
    initData,
    resetState,

    dashboard,
    setDashboard,
    service,
    setService,
    region,
    setRegion,
    saveScore,
    setSaveScore,
  };
};

export default useAwsConfig;
